import {useEffect, useState} from 'react';
import {DataTableSortStatus} from 'mantine-datatable';
// The comparator is SHARED (FNP-912): nulls-last in both directions and numeric-vs-string
// comparison live in one place rather than being re-derived per table.
import {sortRecords} from '@flexinet/ui-components';

import {UsageEvent, UsageEventRow} from '../types';
import {billableStatusRank} from '../utils/billableStatus';
import {DEFAULT_SORT_STATUS, MAX_AUTO_DRAIN_PAGES} from '../config';
import {useUsageEventsFilter} from './useUsageEventsFilter';
import {useUsageEventsSearch} from './useUsageEventsSearch';
import {useUsageEventsInfiniteQuery} from './useUsageEventsQuery';

// FNP-536: read-only module. No create/update/delete — the FNP-215 ingestion writer owns
// the UsageEvent lifecycle. This hook is list-fetch only.

const matchesKeyword = (item: UsageEvent, needle: string): boolean => {
    if (!needle) {
        return true;
    }
    const haystack = [item.eventId, item.meterCode, item.customerId, item.accountId, item.id];
    return haystack.some(value => typeof value === 'string' && value.toLowerCase().includes(needle));
};

const isUsageEvent = (item: UsageEvent | null | undefined): item is UsageEvent => !!item;

export const useUsageEvents = () => {
    const {filters} = useUsageEventsFilter();
    const {keyword} = useUsageEventsSearch();
    const query = useUsageEventsInfiniteQuery(filters);

    // Component state, not the module-scoped store: sort order is how ONE operator is
    // looking at the table right now, unlike keyword and filters, which sibling
    // components share (FNP-815).
    const [sortStatus, setSortStatus] = useState<DataTableSortStatus<UsageEventRow>>(DEFAULT_SORT_STATUS);

    const pages = query.data?.pages ?? [];
    const items: UsageEvent[] = pages.flatMap(page => (page && 'items' in page) ? page.items : [])
                                     .filter(isUsageEvent);

    // PAGE UNDER-FILL (FNP-594, measured in FNP-1883): the drawer filters are non-key
    // FilterExpressions, so a filtered page can come back empty with a nextToken. Keep
    // paging while nothing has loaded, up to MAX_AUTO_DRAIN_PAGES, so a filter that
    // matches on page 4 does not render as "no events". Past the cap the table says more
    // remain and Load more continues by hand.
    const shouldDrain = items.length === 0
        && query.hasNextPage
        && !query.isFetching
        && !query.isError
        && pages.length < MAX_AUTO_DRAIN_PAGES;

    // Keyed on the page count as well: the in-flight render where isFetching is true can
    // be batched away, leaving shouldDrain `true` across two commits with nothing else in
    // the deps changing, and the drain would stall after the second page.
    const {fetchNextPage} = query;
    const pageCount = pages.length;
    useEffect(() => {
        if (shouldDrain) {
            fetchNextPage();
        }
    }, [shouldDrain, pageCount, fetchNextPage]);

    const needle = keyword.trim().toLowerCase();

    // Sorting and keyword matching are CLIENT-SIDE, over the pages LOADED SO FAR. Token
    // pagination cannot reorder or search rows the browser has not fetched; that is a
    // property of the model, not an oversight. The default (receivedAt desc) agrees with
    // the API's own newest-first order, and is still applied here because pages are
    // concatenated as they arrive.
    //
    // Each row carries `status`, the rank of the badge the Status cell renders, so that
    // column sorts by what it shows rather than by a field the wire does not have.
    const rows: UsageEventRow[] = items.filter(item => matchesKeyword(item, needle))
                                       .map(item => ({...item, status: billableStatusRank(item)}));
    const visibleItems = sortRecords(rows, sortStatus);

    return {
        // The full loaded set BEFORE keyword filtering — what the footer's
        // "N of M loaded" is measured against.
        items,
        visibleItems,
        keyword,
        filters,
        sortStatus,
        setSortStatus,
        loading: query.isLoading || shouldDrain || (items.length === 0 && query.isFetchingNextPage),
        isError: query.isError,
        nextToken: query.hasNextPage ? 'has-more' : null,
        isFetchingMore: query.isFetchingNextPage,
        // True when the drain stopped at its cap with nothing matched and more remains.
        drainCapped: items.length === 0 && !!query.hasNextPage && pages.length >= MAX_AUTO_DRAIN_PAGES,
        fetchRecords: () => {
            if (query.hasNextPage && !query.isFetchingNextPage) {
                query.fetchNextPage();
            }
        },
        reload: () => query.refetch(),
    };
};
